"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";

interface DeploymentBannerProps {
  /** Banner text to display. Parent (server component) is responsible for
   * deciding whether to render this at all — pass a non-empty string only
   * when the deployment wants the banner visible. */
  text: string;
}

/**
 * Dismissible sticky banner shown on non-prod deployments so the current
 * environment (and Airtable base) is visible at a glance.
 *
 * Mounted from the root layout, gated by the server-side `DEPLOYMENT_BANNER`
 * env var:
 *   - Unset / empty → server-side check omits this component entirely (no
 *     hydration, no client bundle penalty on prod).
 *   - Set → banner displays the given string; user can dismiss for the
 *     remainder of that browser's localStorage lifetime.
 *
 * Dismiss state is keyed by the banner text so that changing the env value
 * (e.g. pointing at a different Airtable base) re-shows the banner on next
 * page load — the user explicitly re-acknowledges the new context.
 */
export function DeploymentBanner({ text }: DeploymentBannerProps) {
  // Start hidden to avoid a flash if the user has already dismissed.
  // The effect below flips it back to visible if no dismiss is recorded.
  const [hidden, setHidden] = useState(true);
  const storageKey = `deployment-banner-dismissed:${text}`;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const wasDismissed = window.localStorage.getItem(storageKey) === "1";
    setHidden(wasDismissed);
  }, [storageKey]);

  if (hidden) return null;

  return (
    <div
      role="status"
      className="sticky top-0 z-[100] flex items-center justify-between gap-4 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900"
    >
      <span className="font-mono">{text}</span>
      <button
        type="button"
        onClick={() => {
          window.localStorage.setItem(storageKey, "1");
          setHidden(true);
        }}
        className="rounded p-1 text-amber-700 transition-colors hover:bg-amber-200 hover:text-amber-900"
        aria-label="Dismiss deployment banner"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
